import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useToast } from 'components/Providers/ToastProvider';
import axiosInstance from 'config/axiosConfig';
import { useNavigate } from 'react-router-dom';
import useAddRequestStore from 'store/addRequestStore';
import usePropertyRequestsStore from 'store/propertyRequestsStore';
import { PropertyLead, PropertyLeadRequest } from 'types/PropertyLead';
import { PropertyLeadsQuery } from 'types/PropertyLeadsQuery';
import { ROUTES } from 'util/Constants';

function createQueryString(params: Partial<PropertyLeadsQuery>): string {
    const filteredParams = Object.entries(params).filter(([_, value]) => {
        if (Array.isArray(value) && value.length === 0) return false;
        return value !== null && value !== undefined;
    });

    const searchParams = new URLSearchParams();

    filteredParams.forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            if (typeof value === 'number') {
                searchParams.append(key, value.toString());
            } else if (typeof value === 'boolean') {
                searchParams.append(key, value.toString());
            } else if (Array.isArray(value)) {
                searchParams.append(key, value.join(','));
            } else {
                searchParams.append(key, value as string);
            }
        }
    });

    return searchParams.toString();
}

async function fetchPropertyResults(queryFilters: Partial<PropertyLeadsQuery>, isRemoveUser?: boolean) {
    try {
        const queryString = createQueryString(queryFilters);
        return axiosInstance(`/property-leads?${queryString}&isRemoveUser=${isRemoveUser}`);
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
    }
}

async function fetchSavedPropertyRequests(page?: number) {
    try {
        return axiosInstance(`/property-leads/saved?page=${page! + 1 || 1}`);
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
    }
}

interface SavedOrRemovePropertyRequest {
    propertyLeadId: string;
    action: 'save' | 'remove';
}

async function savedOrRemovePropertyRequest({ propertyLeadId, action }: SavedOrRemovePropertyRequest) {
    try {
        return axiosInstance.post(`/property-leads/saveOrRemove`, {
            propertyLeadId,
            action,
        });
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
    }
}

export const useGetPropertyRequests = (queryFilters: PropertyLeadsQuery, isRemoveUser: boolean = true) => {
    return useQuery({
        queryKey: ['getPropertyRequests', queryFilters],
        queryFn: () => fetchPropertyResults(queryFilters, isRemoveUser),
        staleTime: 60 * 5 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useSavedPropertyRequests = () => {
    const { savedRequestsPage } = usePropertyRequestsStore();
    return useQuery({
        queryKey: ['savedPropertyRequests', savedRequestsPage],
        queryFn: () => fetchSavedPropertyRequests(savedRequestsPage),
        staleTime: 60 * 5 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useSavedOrRemovePropertyRequest = () => {
    const queryClient = useQueryClient();
    const { showMessage } = useToast();
    const { propertyRequestQueryFilter, savedRequestsPage, page } = usePropertyRequestsStore();

    return useMutation({
        mutationFn: (data: SavedOrRemovePropertyRequest) => savedOrRemovePropertyRequest(data),
        onMutate: async (data) => {
            await queryClient.cancelQueries({
                queryKey: ['savedPropertyRequests', savedRequestsPage],
            });

            // Get the previous data safely
            const previousData = (queryClient.getQueryData(['savedPropertyRequests', savedRequestsPage]) as Record<string, any>) || {};

            // When updating the saved property requests query data,
            // check if the structure exists before accessing its nested properties.
            if (data.action === 'remove') {
                queryClient.setQueryData(['savedPropertyRequests', savedRequestsPage], (oldData: any) => {
                    if (!oldData || !oldData.data || !oldData.data.data) return oldData;
                    const newLeads = oldData.data.data.leads?.filter((item: Record<string, any>) => item.id !== data.propertyLeadId);
                    const newTotalCount = oldData.data.data.totalCount ? oldData.data.data.totalCount - 1 : 0;
                    return {
                        ...oldData,
                        data: {
                            ...oldData.data,
                            data: {
                                ...oldData.data.data,
                                leads: newLeads,
                                totalCount: newTotalCount,
                            },
                        },
                    };
                });
            }

            if (data.action === 'save') {
                await queryClient.cancelQueries({
                    queryKey: ['savedPropertyRequests', savedRequestsPage],
                });
                const allPropertyRequests = queryClient.getQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }]) as Record<
                    string,
                    any
                >;

                const newPropertyRequests = allPropertyRequests?.data?.data?.leads?.filter((item: Record<string, any>) => item.id === data.propertyLeadId);

                queryClient.setQueryData(['savedPropertyRequests', savedRequestsPage], (oldData: any) => {
                    if (!oldData || !oldData.data || !oldData.data.data) {
                        return {
                            data: {
                                data: {
                                    leads: newPropertyRequests || [],
                                    totalCount: newPropertyRequests?.length || 0,
                                },
                            },
                        };
                    }
                    return {
                        ...oldData,
                        data: {
                            ...oldData.data,
                            data: {
                                ...oldData.data.data,
                                leads: [...(oldData.data.data.leads || []), ...(newPropertyRequests || [])],
                                totalCount: (oldData.data.data.totalCount || 0) + (newPropertyRequests?.length || 0),
                            },
                        },
                    };
                });
            }

            return () => queryClient.setQueryData(['savedPropertyRequests', savedRequestsPage], previousData);
        },
        onSuccess: (_, data) => {
            showMessage('Success', `Property request ${data.action === 'save' ? 'saved' : 'removed'} successfully`);
        },
        onError: (error, _, rollback) => {
            rollback?.();
            showMessage('Error', (error as any).response?.data.message);
        },
    });
};

async function updatePropertyRequest({ propertyLeadId, isArchived }: { propertyLeadId: string; isArchived: boolean }) {
    try {
        return axiosInstance.patch(`/property-leads/${propertyLeadId}`, {
            isArchived,
        });
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
    }
}

export const useUpdatePropertyRequest = () => {
    const queryClient = useQueryClient();
    const { savedRequestsPage, propertyRequestQueryFilter, page } = usePropertyRequestsStore();
    const { showMessage } = useToast();
    return useMutation({
        mutationFn: (data: { propertyLeadId: string; isArchived: boolean }) => updatePropertyRequest(data),
        onMutate: async (data) => {
            await queryClient.cancelQueries({
                queryKey: ['getPropertyRequests', { isArchived: false }],
            });
            await queryClient.cancelQueries({
                queryKey: ['getPropertyRequests', { isArchived: true }],
            });
            await queryClient.cancelQueries({
                queryKey: ['savedPropertyRequests', savedRequestsPage],
            });
            await queryClient.cancelQueries({
                queryKey: ['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }],
            });

            const updateQueryData = (key: any, newLeads: any, newTotal: number) => {
                queryClient.setQueryData(key, (oldData: Record<string, any>) => ({
                    ...oldData,
                    data: {
                        ...oldData?.data,
                        data: {
                            ...oldData?.data?.data,
                            leads: newLeads,
                            total: newTotal,
                        },
                    },
                }));
            };
            const previousOpen = (queryClient.getQueryData(['getPropertyRequests', { isArchived: false }]) as Record<string, any>) || {};
            const previousArchived = (queryClient.getQueryData(['getPropertyRequests', { isArchived: true }]) as Record<string, any>) || {};
            const previousDataForSavedPropertyRequests = (queryClient.getQueryData(['savedPropertyRequests', savedRequestsPage]) as Record<string, any>) ?? {};
            const previousAllPropertyRequests = queryClient.getQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }]) as Record<
                string,
                any
            >;

            const previousOpenLeads = previousOpen?.data?.data?.leads || [];
            const previousArchivedLeads = previousArchived?.data?.data?.leads || [];
            const previousOpenTotal = previousOpen?.data?.data?.total || 0;
            const previousArchivedTotal = previousArchived?.data?.data?.total || 0;
            const previousSavedPropertyRequests = previousDataForSavedPropertyRequests?.data?.data?.leads || [];
            const previousSavedPropertyRequestsTotal = previousDataForSavedPropertyRequests?.data?.data?.totalCount || 0;
            const allPropertyRequests = previousAllPropertyRequests?.data?.data?.leads || [];
            const allPropertyRequestsTotal = previousAllPropertyRequests?.data?.data?.total || 0;

            const isArchived = data.isArchived;
            const targetList = isArchived ? previousOpenLeads : previousArchivedLeads;
            const updatedSourceList = targetList.filter((item: PropertyLead) => item.id !== data.propertyLeadId);
            const movedItem = targetList.find((item: PropertyLead) => item.id === data.propertyLeadId);

            if (isArchived) {
                const updatedSavedPropertyRequests = previousSavedPropertyRequests?.filter((item: PropertyLead) => item?.id !== data.propertyLeadId);
                queryClient.setQueryData(['savedPropertyRequests', savedRequestsPage], {
                    ...previousDataForSavedPropertyRequests,
                    data: {
                        ...previousDataForSavedPropertyRequests?.data,
                        data: {
                            ...previousDataForSavedPropertyRequests?.data?.data,
                            leads: updatedSavedPropertyRequests,
                            totalCount: previousSavedPropertyRequestsTotal - 1,
                        },
                    },
                });
                const updatedAllPropertyRequests = allPropertyRequests?.filter((item: PropertyLead) => item?.id !== data.propertyLeadId);
                queryClient.setQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }], {
                    ...previousAllPropertyRequests,
                    data: {
                        ...previousAllPropertyRequests?.data,
                        data: { ...previousAllPropertyRequests?.data?.data, leads: updatedAllPropertyRequests, total: allPropertyRequestsTotal - 1 },
                    },
                });
            } else {
                const updatedSavedPropertyRequests = [...previousSavedPropertyRequests, movedItem];
                queryClient.setQueryData(['savedPropertyRequests', savedRequestsPage], {
                    ...previousDataForSavedPropertyRequests,
                    data: {
                        ...previousDataForSavedPropertyRequests?.data,
                        data: {
                            ...previousDataForSavedPropertyRequests?.data?.data,
                            leads: updatedSavedPropertyRequests,
                            totalCount: previousSavedPropertyRequestsTotal + 1,
                        },
                    },
                });
                const updatedAllPropertyRequests = [...allPropertyRequests, movedItem];
                queryClient.setQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }], {
                    ...previousAllPropertyRequests,
                    data: {
                        ...previousAllPropertyRequests?.data,
                        data: { ...previousAllPropertyRequests?.data?.data, leads: updatedAllPropertyRequests, total: allPropertyRequestsTotal + 1 },
                    },
                });
            }

            if (movedItem) {
                updateQueryData(
                    ['getPropertyRequests', { isArchived: false }],
                    isArchived ? updatedSourceList : [...previousOpenLeads, movedItem],
                    isArchived ? previousOpenTotal - 1 : previousOpenTotal + 1,
                );

                updateQueryData(
                    ['getPropertyRequests', { isArchived: true }],
                    isArchived ? [...previousArchivedLeads, { ...movedItem, archivedAt: new Date() }] : updatedSourceList,
                    isArchived ? previousArchivedTotal + 1 : previousArchivedTotal - 1,
                );
            }
            showMessage('Success', `Property request ${isArchived ? 'archived' : 'unarchived'} successfully`);
            return () => {
                queryClient.setQueryData(['getPropertyRequests', { isArchived: false }], previousOpen);
                queryClient.setQueryData(['getPropertyRequests', { isArchived: true }], previousArchived);
                queryClient.setQueryData(['savedPropertyRequests', savedRequestsPage], previousDataForSavedPropertyRequests);
                queryClient.setQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }], previousAllPropertyRequests);
            };
        },
        onError: (error, _, rollback) => {
            rollback?.();
            showMessage('Error', (error as any).response?.data.message);
        },
    });
};

async function deletePropertyRequest(propertyLeadId: string) {
    try {
        return axiosInstance.delete(`/property-leads/${propertyLeadId}`);
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
    }
}

export const useDeletePropertyRequest = () => {
    const queryClient = useQueryClient();
    const { propertyRequestQueryFilter, page } = usePropertyRequestsStore();
    const { showMessage } = useToast();
    return useMutation({
        mutationFn: (propertyLeadId: string) => deletePropertyRequest(propertyLeadId),
        onMutate: async (propertyLeadId) => {
            await queryClient.cancelQueries({
                queryKey: ['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }],
            });
            await queryClient.cancelQueries({
                queryKey: ['getPropertyRequests', { isArchived: true }],
            });
            const previousData =
                (queryClient.getQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }]) as Record<string, any>) || [];
            const previousArchivedData = (queryClient.getQueryData(['getPropertyRequests', { isArchived: true }]) as Record<string, any>) || [];
            const updatedOpenData = previousData.data.data.leads?.filter((item: Record<string, any>) => item.id !== propertyLeadId);
            const updatedArchivedData = previousArchivedData.data.data.leads?.filter((item: Record<string, any>) => item.id !== propertyLeadId);
            queryClient.setQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }], {
                ...previousData,
                data: { ...previousData.data, data: { ...previousData.data.data, leads: updatedOpenData, total: previousData.data.data.total - 1 } },
            });
            queryClient.setQueryData(['getPropertyRequests', { isArchived: true }], {
                ...previousArchivedData,
                data: {
                    ...previousArchivedData.data,
                    data: { ...previousArchivedData.data.data, leads: updatedArchivedData, total: previousArchivedData.data.data.total - 1 },
                },
            });
            showMessage('Success', `Property request deleted successfully`);
            return () => {
                queryClient.setQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }], previousData);
                queryClient.setQueryData(['getPropertyRequests', { isArchived: true }], previousArchivedData);
            };
        },
        onError: (error, _, rollback) => {
            rollback?.();
            showMessage('Error', (error as any).response?.data.message);
        },
    });
};

async function createPropertyRequest(propertyRequest: PropertyLeadRequest) {
    try {
        return axiosInstance.post('/property-leads', propertyRequest);
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
    }
}

export const useCreatePropertyRequest = () => {
    const queryClient = useQueryClient();
    const { showMessage } = useToast();
    const navigate = useNavigate();
    const { propertyRequestQueryFilter, page } = usePropertyRequestsStore();
    const { clearRequestData } = useAddRequestStore();
    return useMutation({
        mutationFn: (propertyRequest: PropertyLeadRequest) => createPropertyRequest(propertyRequest),
        onMutate: async (propertyRequest) => {
            const queryKey = ['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }];
            await queryClient.cancelQueries({ queryKey });

            const previousData = (queryClient.getQueryData(queryKey) as Record<string, any>) || { data: { data: { leads: [], total: 0 } } };

            queryClient.setQueryData(queryKey, {
                ...previousData,
                data: {
                    ...previousData.data,
                    data: {
                        ...previousData.data.data,
                        leads: [...previousData.data.data.leads, { ...propertyRequest, id: Math.random().toString(), createdAt: new Date().toISOString() }],
                        total: previousData.data.data.total + 1,
                    },
                },
            });

            return () => {
                queryClient.setQueryData(queryKey, previousData);
            };
        },
        onError: (error, _, rollback) => {
            rollback?.();
            showMessage('Error', (error as any).response?.data.message);
        },
        onSuccess: () => {
            navigate(ROUTES.MAIN);
            clearRequestData();
            queryClient.invalidateQueries({ queryKey: ['getPropertyRequests'] });
            queryClient.invalidateQueries({ queryKey: ['amenities'] });
        },
    });
};

async function downloadCSV() {
    try {
        const { data } = await axiosInstance.get(`/property-leads/download-csv`);
        return data.data.url as string;
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
        throw new Error(error as string);
    }
}

export const useDownloadCSVPropertyRequests = () => {
    return useMutation({
        mutationFn: downloadCSV,
        onSuccess: async (url: string) => {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', 'property-requests.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        },
    });
};

async function fetchAmenities() {
    try {
        const response = await axiosInstance.get(`/property-leads/amenities`);
        return response.data.data || response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
        throw new Error('An unexpected error occurred while fetching amenities');
    }
}

export const useGetAmenities = () => {
    return useQuery({
        queryKey: ['amenities'],
        queryFn: fetchAmenities,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

async function fetchBuyerProfiles() {
    try {
        const response = await axiosInstance.get(`/property-leads/buyer-profiles`);
        return response.data.data || response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
        throw new Error('An unexpected error occurred while fetching buyer profiles');
    }
}

export const useGetBuyerProfiles = () => {
    return useQuery({
        queryKey: ['buyerProfiles'],
        queryFn: fetchBuyerProfiles,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};
